import { useState, type FormEvent } from 'react';
import { IconButton, MailIcon, PauseIcon, PlayIcon } from './Icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type PortalOverview, type PortalUserView } from '../lib/api';
import { relativeTime } from '../lib/format';

// Usuarios del portal del cliente, por cliente (solo administrador).

const input = 'h-9 rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm focus:border-accent focus:outline-none';
const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const ROLE = { requester: 'Solicitante', approver: 'Aprobador' } as const;

export function PortalSettings() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['admin-portal'], queryFn: () => api<PortalOverview>('/admin/portal') });
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [form, setForm] = useState({ email: '', name: '', organizationId: '', role: 'requester', locale: 'es' });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admin-portal'] });
  const fail = (e: unknown) => setMsg({ kind: 'error', text: e instanceof ApiError ? e.message : 'Algo salió mal.' });

  const create = useMutation({
    mutationFn: () => api<{ invited: boolean }>('/admin/portal/users', { method: 'POST', body: JSON.stringify({ ...form, sendInvite: true }) }),
    onSuccess: r => {
      setMsg({ kind: 'ok', text: r.invited ? `Invitación enviada a ${form.email}.` : `Usuario guardado. No salió la invitación (conecta el buzón en Correo).` });
      setForm(f => ({ ...f, email: '', name: '' })); refresh();
    },
    onError: fail
  });
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Partial<PortalUserView> }) => api(`/admin/portal/users/${encodeURIComponent(v.id)}`, { method: 'PATCH', body: JSON.stringify(v.body) }),
    onSuccess: () => { setMsg(null); refresh(); },
    onError: fail
  });
  const resend = useMutation({
    mutationFn: (u: PortalUserView) => api<{ invited: boolean }>(`/admin/portal/users/${encodeURIComponent(u.id)}/invite`, { method: 'POST' }),
    onSuccess: (r, u) => setMsg(r.invited ? { kind: 'ok', text: `Invitación reenviada a ${u.email}.` } : { kind: 'error', text: 'No salió: conecta el buzón en Correo.' }),
    onError: fail
  });

  if (query.isLoading) return <p role="status" className="py-6 text-sm text-muted">Cargando…</p>;
  if (!query.data) return <p role="alert" className="text-sm text-[#8F1D1D]">No se pudo cargar el portal.</p>;
  const data = query.data;
  const submit = (e: FormEvent) => { e.preventDefault(); create.mutate(); };

  return (
    <div className="flex max-w-4xl flex-col gap-5">
      <div className="rounded-lg border border-line bg-white p-4 text-sm">
        <p>Liga del portal para tus clientes:</p>
        <p className="mt-1 flex flex-wrap items-center gap-2">
          <a href={data.portalUrl} target="_blank" rel="noreferrer" className="font-mono text-accent underline-offset-2 hover:underline">{data.portalUrl}</a>
          <button type="button" className="text-xs text-muted hover:text-ink" onClick={() => void navigator.clipboard?.writeText(data.portalUrl)}>Copiar</button>
        </p>
        <p className="mt-2 text-xs text-muted">
          Entran con un código que les llega al correo (sin contraseña). Quien escriba desde un dominio registrado de un cliente
          (pestaña Correo) se reconoce solo como <strong>solicitante</strong>; los <strong>aprobadores</strong> se invitan aquí.
        </p>
      </div>

      {msg && <p role={msg.kind === 'error' ? 'alert' : 'status'} className={`rounded-md px-3 py-2 text-sm ${msg.kind === 'error' ? 'bg-[#FBEAEA] text-[#8F1D1D]' : 'bg-accent-soft text-accent-dark'}`}>{msg.text}</p>}

      <form onSubmit={submit} className="flex flex-wrap items-end gap-2 rounded-lg border border-line bg-white p-4">
        <p className="w-full text-sm font-semibold">Invitar a alguien del cliente</p>
        <label className="flex flex-col gap-1 text-xs text-muted">Cliente
          <select required className={input} value={form.organizationId} onChange={e => setForm(f => ({ ...f, organizationId: e.target.value }))}>
            <option value="">Elige…</option>
            {data.organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted">Nombre<input required className={input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></label>
        <label className="flex flex-col gap-1 text-xs text-muted">Correo<input required type="email" className={input} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></label>
        <label className="flex flex-col gap-1 text-xs text-muted">Rol
          <select className={input} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
            <option value="requester">Solicitante</option><option value="approver">Aprobador</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted">Idioma
          <select className={input} value={form.locale} onChange={e => setForm(f => ({ ...f, locale: e.target.value }))}>
            <option value="es">Español</option><option value="en">English</option>
          </select>
        </label>
        <button type="submit" className={btnPrimary} disabled={create.isPending}>Invitar</button>
      </form>

      {data.organizations.map(o => (
        <section key={o.id} className="rounded-lg border border-line bg-white">
          <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-4 py-2.5">
            <h3 className="text-sm font-semibold">{o.name}</h3>
            <span className="text-xs text-muted">{o.domains.length ? o.domains.map(d => `@${d.replace(/^@/, '')}`).join(' · ') : 'sin dominio registrado'}</span>
          </header>
          {o.users.length === 0 ? <p className="px-4 py-3 text-xs text-muted">Sin usuarios en el portal.</p> : (
            <table className="w-full border-collapse text-sm">
              <tbody>
                {o.users.map(u => (
                  <tr key={u.id} className={`border-b border-[#EFEDE9] last:border-0 ${u.active ? '' : 'opacity-55'}`}>
                    <td className="px-4 py-2"><span className="font-medium">{u.name}</span><span className="block text-xs text-muted">{u.email}{u.source === 'domain' ? ' · por dominio' : ''}</span></td>
                    <td className="px-2 py-2">
                      <select aria-label={`Rol de ${u.name}`} className={input} value={u.role} disabled={!u.active}
                        onChange={e => patch.mutate({ id: u.id, body: { role: e.target.value as PortalUserView['role'] } })}>
                        {(Object.keys(ROLE) as (keyof typeof ROLE)[]).map(r => <option key={r} value={r}>{ROLE[r]}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2 text-xs text-muted">{u.locale === 'en' ? 'English' : 'Español'}</td>
                    <td className="px-2 py-2 text-xs text-muted">{u.lastLoginAt ? `Entró ${relativeTime(u.lastLoginAt)}` : 'No ha entrado'}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      {u.active && <IconButton label={`Reenviar invitación a ${u.name}`} tone="accent" onClick={() => resend.mutate(u)}><MailIcon /></IconButton>}
                      <IconButton label={u.active ? `Desactivar a ${u.name}` : `Activar a ${u.name}`} tone={u.active ? 'default' : 'success'}
                        onClick={() => patch.mutate({ id: u.id, body: { active: !u.active } })}>{u.active ? <PauseIcon /> : <PlayIcon />}</IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ))}
    </div>
  );
}
